// File: utils/account.js

/**
 * CDRisk Account client for the regulations viewer.
 *
 * Talks to the central CDRisk API for password, passkey (WebAuthn), and
 * social (Google / Microsoft / Apple) sign-in. All methods return the same
 * session shape: { user, token, refreshToken }, stored in localStorage.
 * Annotation sync (utils/annotationSync.js) rides on this session.
 */

import { ENV_CONFIG } from '../config/environments.js';
import { getItem, setItem, removeItem } from './storage.js';

const API_URL = (ENV_CONFIG.API_URL || 'https://cdrisk-api-production.up.railway.app').replace(/\/+$/, '');
const ACCOUNT_KEY = `${ENV_CONFIG.STORAGE_PREFIX}account`;

// ========================================
// Session state
// ========================================

export function getAccount() {
  const stored = getItem(ACCOUNT_KEY, null);
  return stored && stored.token && stored.user ? stored : null;
}

export function isSignedIn() {
  return !!getAccount();
}

function storeSession(session) {
  setItem(ACCOUNT_KEY, {
    token: session.token,
    refreshToken: session.refreshToken,
    user: session.user,
  });
  notifyChange();
  return getAccount();
}

export function signOut() {
  removeItem(ACCOUNT_KEY);
  notifyChange();
}

function notifyChange() {
  window.dispatchEvent(new CustomEvent('cdrisk:account-changed'));
}

// ========================================
// API helper
// ========================================

export async function apiFetch(path, { method = 'GET', body, auth = false } = {}) {
  const headers = { 'Content-Type': 'application/json' };
  if (auth) {
    const account = getAccount();
    if (!account) throw new Error('Not signed in');
    headers.Authorization = `Bearer ${account.token}`;
  }
  const res = await fetch(`${API_URL}${path}`, {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  const data = await res.json().catch(() => ({}));
  if (res.status === 401 && auth) {
    // Expired/invalid token — try one silent refresh, then retry once
    const refreshed = await tryRefresh();
    if (refreshed) {
      headers.Authorization = `Bearer ${refreshed.token}`;
      const retry = await fetch(`${API_URL}${path}`, {
        method,
        headers,
        body: body !== undefined ? JSON.stringify(body) : undefined,
      });
      const retryData = await retry.json().catch(() => ({}));
      if (!retry.ok) throw new Error(retryData.error || `Request failed (${retry.status})`);
      return retryData;
    }
    signOut();
  }
  if (!res.ok) throw new Error(data.error || `Request failed (${res.status})`);
  return data;
}

async function tryRefresh() {
  const account = getAccount();
  if (!account?.refreshToken) return null;
  try {
    const res = await fetch(`${API_URL}/api/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken: account.refreshToken }),
    });
    if (!res.ok) return null;
    const data = await res.json();
    if (data.token) {
      setItem(ACCOUNT_KEY, { ...account, token: data.token, refreshToken: data.refreshToken || account.refreshToken });
      return getAccount();
    }
  } catch {
    /* network error — treat as signed out */
  }
  return null;
}

// ========================================
// Password + registration
// ========================================

export async function signInWithPassword(email, password) {
  const data = await apiFetch('/api/auth/login', { method: 'POST', body: { email, password } });
  return storeSession(data.data || data);
}

export async function registerAccount({ email, password, firstName, lastName }) {
  const data = await apiFetch('/api/auth/account/register', {
    method: 'POST',
    body: { email, password, firstName, lastName },
  });
  return storeSession(data);
}

// ========================================
// Social sign-in (Google / Microsoft / Apple)
// ========================================

export async function fetchProviders() {
  try {
    const data = await apiFetch('/api/auth/oauth/providers');
    return Array.isArray(data.providers) ? data.providers : [];
  } catch {
    return [];
  }
}

export function startOAuth(provider) {
  const returnTo = `${window.location.origin}${window.location.pathname}`;
  window.location.href = `${API_URL}/api/auth/oauth/${provider}/start?return_to=${encodeURIComponent(returnTo)}`;
}

/**
 * Complete a social sign-in after the provider redirects back with
 * ?cdrisk_code=. Returns the account on success, null when no code present.
 * Also surfaces ?cdrisk_error= as a thrown Error.
 */
export async function completeOAuthRedirect() {
  const params = new URLSearchParams(window.location.search);
  const code = params.get('cdrisk_code');
  const error = params.get('cdrisk_error');
  if (!code && !error) return null;

  // Strip the params so refreshes don't retry a consumed code
  params.delete('cdrisk_code');
  params.delete('cdrisk_error');
  const query = params.toString();
  window.history.replaceState({}, '', `${window.location.pathname}${query ? `?${query}` : ''}`);

  if (error) {
    const messages = {
      cancelled: 'Sign-in was cancelled.',
      link_required:
        'An account with this email already exists. Sign in with your password, then add this provider.',
      provider_error: 'The sign-in provider reported an error. Please try again.',
      exchange_failed: 'Sign-in could not be completed. Please try again.',
      signin_failed: 'Sign-in could not be completed. Please try again.',
    };
    throw new Error(messages[error] || messages.signin_failed);
  }

  const data = await apiFetch('/api/auth/oauth/code/exchange', { method: 'POST', body: { code } });
  return storeSession(data);
}

// ========================================
// Passkeys (WebAuthn)
// ========================================

export function passkeysSupported() {
  return typeof window !== 'undefined' && !!window.PublicKeyCredential;
}

function b64uToBuffer(value) {
  const padded = value.replace(/-/g, '+').replace(/_/g, '/').padEnd(Math.ceil(value.length / 4) * 4, '=');
  return Uint8Array.from(atob(padded), (c) => c.charCodeAt(0));
}

function bufferToB64u(buffer) {
  return btoa(String.fromCharCode(...new Uint8Array(buffer)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

export async function signInWithPasskey() {
  const optionsRes = await apiFetch('/api/auth/passkeys/authenticate/options', { method: 'POST' });
  const options = optionsRes.data;
  const credential = await navigator.credentials.get({
    publicKey: {
      ...options,
      challenge: b64uToBuffer(options.challenge),
      allowCredentials: [],
    },
  });
  if (!credential) throw new Error('Passkey sign-in was cancelled.');

  const payload = {
    id: credential.id,
    rawId: bufferToB64u(credential.rawId),
    type: credential.type,
    clientExtensionResults: credential.getClientExtensionResults?.() || {},
    response: {
      clientDataJSON: bufferToB64u(credential.response.clientDataJSON),
      authenticatorData: bufferToB64u(credential.response.authenticatorData),
      signature: bufferToB64u(credential.response.signature),
      userHandle: credential.response.userHandle ? bufferToB64u(credential.response.userHandle) : null,
    },
  };
  const data = await apiFetch('/api/auth/passkeys/authenticate/verify', {
    method: 'POST',
    body: { response: payload },
  });
  return storeSession(data.data || data);
}

export async function registerPasskey(deviceName = 'This device') {
  const optionsRes = await apiFetch('/api/auth/passkeys/register/options', {
    method: 'POST',
    auth: true,
  });
  const options = optionsRes.data;
  const credential = await navigator.credentials.create({
    publicKey: {
      ...options,
      challenge: b64uToBuffer(options.challenge),
      user: { ...options.user, id: b64uToBuffer(options.user.id) },
      excludeCredentials: (options.excludeCredentials || []).map((c) => ({
        ...c,
        id: b64uToBuffer(c.id),
      })),
    },
  });
  if (!credential) throw new Error('Passkey setup was cancelled.');

  const payload = {
    id: credential.id,
    rawId: bufferToB64u(credential.rawId),
    type: credential.type,
    clientExtensionResults: credential.getClientExtensionResults?.() || {},
    response: {
      clientDataJSON: bufferToB64u(credential.response.clientDataJSON),
      attestationObject: bufferToB64u(credential.response.attestationObject),
      transports: credential.response.getTransports?.() || [],
    },
  };
  await apiFetch('/api/auth/passkeys/register/verify', {
    method: 'POST',
    auth: true,
    body: { response: payload, deviceName },
  });
  return true;
}
